import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useSEO } from "../hooks/useSEO";
import WhatsAppCTA from "../components/WhatsAppCTA";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_IMAGES = [
  "/assets/projects/chi-chi-mar/01_dsc02713.jpg",
  "/assets/projects/kitchen/01_dsc03664.jpg",
  "/assets/projects/clements/01_render.jpg",
  "/assets/projects/jardin-nacho/01_dsc00535.jpg",
  "/assets/projects/casa-roca/img_20231127_114801.jpg",
  "/assets/projects/chi-chi-mar/03_dsc02719.jpg"
];

const categories = [
  { id: "all", label: "Todos" },
  { id: "diseño_integral", label: "Diseño Integral" },
  { id: "proyectos_estrategicos", label: "Proyectos Estratégicos" },
  { id: "mantenimiento", label: "Mantenimiento" }
];

const demoProjects = [
  {
    id: "1",
    title: "Residencia Palmilla",
    description: "Diseño naturalista integral con sistema de riego inteligente",
    category: "diseño_integral",
    location: "San José del Cabo",
    year: "2025",
    images: [FALLBACK_IMAGES[0]]
  },
  {
    id: "2",
    title: "Casa Pedregal",
    description: "Integración del paisaje con arquitectura contemporánea",
    category: "diseño_integral",
    location: "Cabo San Lucas",
    year: "2025",
    images: [FALLBACK_IMAGES[1]]
  },
  {
    id: "3",
    title: "Desarrollo El Tezal",
    description: "Proyecto estratégico para desarrollo residencial",
    category: "proyectos_estrategicos",
    location: "El Tezal, BCS",
    year: "2024",
    images: [FALLBACK_IMAGES[2]]
  },
  {
    id: "4",
    title: "Villa Punta Ballena",
    description: "Mantenimiento ecosistémico continuo",
    category: "mantenimiento",
    location: "Punta Ballena",
    year: "2024",
    images: [FALLBACK_IMAGES[3]]
  },
  {
    id: "5",
    title: "Hotel Boutique Mar de Cortés",
    description: "Paisajismo comercial con enfoque sostenible",
    category: "proyectos_estrategicos",
    location: "La Paz",
    year: "2024",
    images: [FALLBACK_IMAGES[4]]
  },
  {
    id: "6",
    title: "Rancho San Dionisio",
    description: "Diseño de huerta orgánica y espacios contemplativos",
    category: "diseño_integral",
    location: "Sierra de la Laguna",
    year: "2023",
    images: [FALLBACK_IMAGES[5]]
  }
];

const ProjectCard = ({ project, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      <Link 
        to={`/portfolio/${project.id}`}
        className="group block"
        data-testid={`project-card-${project.id}`}
      >
        <div className="relative overflow-hidden aspect-[4/3] mb-4">
          <img
            src={project.images?.[0] || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length]}
            alt={project.title}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-500" />
          <div className="absolute bottom-0 left-0 right-0 p-6 translate-y-full group-hover:translate-y-0 transition-transform duration-500">
            <span className="btn-secondary inline-block">Ver proyecto</span>
          </div>
        </div>
        <span className="tag mb-2">{project.category.replace("_", " ")}</span>
        <h3 className="text-xl font-serif mb-1 group-hover:text-circulo-sand transition-colors">
          {project.title}
        </h3>
        <p className="text-sm text-muted-foreground">
          {project.location} — {project.year}
        </p>
      </Link>
    </motion.div>
  );
};

export default function Portfolio() {
  useSEO({ title: "Portfolio", description: "Proyectos de paisajismo naturalista en Los Cabos. Diseño integral, proyectos estratégicos y mantenimiento ecosistémico." });
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${API}/projects`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data.length > 0 ? data : demoProjects);
      } else {
        setProjects(demoProjects);
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      setProjects(demoProjects);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = activeCategory === "all" 
    ? projects 
    : projects.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen pt-32 pb-24 bg-[#F9F6F0]" data-testid="portfolio-page">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xs tracking-[0.3em] uppercase text-[#547055] mb-4 block"
          >
            Nuestro Trabajo
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl md:text-6xl lg:text-7xl font-serif font-light mb-6"
          >
            Portfolio
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#547055] max-w-2xl"
          >
            Cada proyecto representa una respuesta única al microclima, suelo y necesidades 
            específicas de cada propiedad en Baja California Sur.
          </motion.p>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap gap-4 mb-12 border-b border-white/10 pb-6"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`text-xs uppercase tracking-widest px-4 py-2 transition-colors ${
                activeCategory === cat.id
                  ? "text-white bg-[#1C2E24]"
                  : "text-[#547055] hover:text-[#1C2E24]"
              }`}
              data-testid={`filter-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </motion.div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] bg-circulo-subtle mb-4" />
                <div className="h-4 bg-circulo-subtle w-1/3 mb-2" />
                <div className="h-6 bg-circulo-subtle w-2/3 mb-1" />
                <div className="h-4 bg-circulo-subtle w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <ProjectCard key={project.id} project={project} index={index} />
            ))}
          </div>
        )}

        {filteredProjects.length === 0 && !loading && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">No hay proyectos en esta categoría.</p>
          </div>
        )}

        {/* WhatsApp CTA */}
        <div className="mt-20">
          <WhatsAppCTA
            source="portfolio_page"
            title="¿Quieres un resultado así para tu propiedad?"
            subtitle="Cuéntanos sobre tu espacio y te decimos cómo podemos transformarlo."
            message="Hola, vi su portfolio y me interesa un proyecto similar para mi propiedad."
            variant="dark"
          />
        </div>
      </div>
    </div>
  );
}
